import React, { useMemo, useState } from "react";
import AppLayout, { PageHeader } from "../components/AppLayout";
import {
    DMS,
    RISK,
    SOPS,
    LINKS,
    RISK_WEIGHT,
    READINESS,
} from "../data/linkGraphSeed";
import {
    AlertTriangle,
    ChevronRight,
    Info,
    Zap,
    Database,
    ArrowRight,
} from "lucide-react";

// ---------- small atoms ----------

const Stat = ({ label, value, sub, accent, testid }) => (
    <div
        className="p-8 border-r border-b border-hair bg-white"
        data-testid={testid}
    >
        <div className="overline mb-4">{label}</div>
        <div
            className={`heading text-4xl font-medium tracking-tighter leading-none ${
                accent ? "text-[#C2410C]" : ""
            }`}
        >
            {value}
        </div>
        {sub && <div className="text-xs text-neutral-500 mt-3 mono">{sub}</div>}
    </div>
);

const DmsBadge = ({ dmsId, size = "sm" }) => {
    const d = DMS[dmsId];
    if (!d) return null;
    const base =
        size === "lg"
            ? "text-[11px] px-2.5 py-[3px]"
            : "text-[10px] px-2 py-[2px]";
    return (
        <span
            className={`mono uppercase tracking-wider border rounded-sm inline-flex items-center gap-1.5 ${base}`}
            style={{
                color: d.accent,
                background: d.accentBg,
                borderColor: d.accentBorder,
            }}
            title={`Source of record: ${d.name}`}
        >
            <span
                className="w-[6px] h-[6px] rounded-sm"
                style={{ background: d.accent }}
            />
            {d.short}
        </span>
    );
};

const RiskPill = ({ riskId }) => {
    const r = RISK[riskId];
    if (!r) return null;
    return (
        <span
            className="mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm inline-flex items-center gap-1"
            style={{
                color: r.color,
                borderColor: r.color + "66",
                background: r.color + "14",
            }}
        >
            {r.label}
        </span>
    );
};

// ---------- triage rail (left 25%) ----------

const TriageRail = ({ links, selectedLinkId, onSelectLink, onFocusSop }) => {
    const sorted = useMemo(
        () =>
            [...links]
                .filter((l) => l.risk !== "aligned")
                .sort(
                    (a, b) =>
                        (RISK_WEIGHT[b.risk] || 0) - (RISK_WEIGHT[a.risk] || 0),
                ),
        [links],
    );

    return (
        <aside
            className="border-r border-hair bg-white flex flex-col"
            data-testid="triage-rail"
        >
            <div className="px-5 py-4 border-b border-hair">
                <div className="overline flex items-center gap-2">
                    <AlertTriangle size={11} /> Remediation queue
                </div>
                <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                    Top inconsistencies ranked by audit exposure. Click any row
                    to spotlight the affected SOP.
                </div>
            </div>
            <div className="overflow-y-auto flex-1">
                {sorted.map((l, i) => {
                    const from = SOPS.find((s) => s.id === l.from);
                    const to = SOPS.find((s) => s.id === l.to);
                    const active = l.id === selectedLinkId;
                    return (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => {
                                onSelectLink(l.id);
                                if (from) onFocusSop(from.id);
                            }}
                            data-testid={`triage-row-${l.id}`}
                            className={`w-full text-left px-5 py-4 border-b border-hair last:border-b-0 transition-colors ${
                                active ? "bg-[#fafafa]" : "hover:bg-neutral-50"
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <span className="mono text-[10px] text-neutral-400">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <RiskPill riskId={l.risk} />
                            </div>
                            <div className="flex items-center gap-2 text-[13px] heading tracking-tight font-medium">
                                <span className="truncate">{from?.id}</span>
                                <ArrowRight
                                    size={12}
                                    className="text-neutral-300 shrink-0"
                                />
                                <span className="truncate">{to?.id}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <DmsBadge dmsId={from?.dms} />
                                <span className="text-neutral-300 text-[10px]">
                                    ↔
                                </span>
                                <DmsBadge dmsId={to?.dms} />
                            </div>
                            <p className="text-[11.5px] text-neutral-600 mt-3 leading-relaxed line-clamp-3">
                                {l.note}
                            </p>
                        </button>
                    );
                })}
            </div>
        </aside>
    );
};

// ---------- spotlight canvas (right 75%) ----------

const SPOTLIGHT_W = 900;
const SPOTLIGHT_H = 540;
const CENTER = { x: SPOTLIGHT_W / 2, y: SPOTLIGHT_H / 2 };
const CENTER_CARD = { w: 360, h: 320 };
const SATELLITE_CARD = { w: 220, h: 110 };

// Even-angle satellite positions around the center
const satellitePositions = (count) => {
    const ringRadiusX = 380;
    const ringRadiusY = 230;
    const out = [];
    for (let i = 0; i < count; i++) {
        // Distribute on an ellipse; skew start so satellites don't stack
        const theta = (-Math.PI / 2) + (i / count) * Math.PI * 2;
        out.push({
            x: CENTER.x + ringRadiusX * Math.cos(theta),
            y: CENTER.y + ringRadiusY * Math.sin(theta),
        });
    }
    return out;
};

// ---------- rule row inside the center SOP card ----------

const RuleRow = ({ rule, highlighted }) => (
    <div
        id={`rule-${rule.id}`}
        data-rule-id={rule.id}
        className={`px-3 py-2 border-b border-hair last:border-b-0 flex items-center gap-2 ${
            highlighted ? "bg-[#FFF7ED]" : ""
        }`}
    >
        <span className="mono text-[10px] text-[#C2410C] shrink-0">
            {rule.id}
        </span>
        <span className="text-[11px] leading-snug text-neutral-700 truncate">
            {rule.text}
        </span>
    </div>
);

const Spotlight = ({
    centerSopId,
    onFocusSop,
    selectedLinkId,
    onSelectLink,
}) => {
    const center = SOPS.find((s) => s.id === centerSopId);
    // Every link whose either end is the center. Dedupe by the OTHER SOP
    // so we show one satellite card per neighbor, with the highest-risk link
    // as the primary edge and a badge showing how many links touch this SOP.
    const adjacencies = useMemo(() => {
        const byOther = new Map();
        LINKS.filter(
            (l) => l.from === centerSopId || l.to === centerSopId,
        ).forEach((l) => {
            const otherId = l.from === centerSopId ? l.to : l.from;
            const other = SOPS.find((s) => s.id === otherId);
            if (!other) return;
            const centerRule =
                l.from === centerSopId ? l.from_rule : l.to_rule;
            const entry = byOther.get(otherId) || {
                other,
                links: [],
                primary: null,
                centerRule: null,
            };
            entry.links.push({ link: l, centerRule });
            // primary = highest-risk link
            const riskRank = (rid) => RISK_WEIGHT[rid] || 0;
            if (
                !entry.primary ||
                riskRank(l.risk) > riskRank(entry.primary.link.risk)
            ) {
                entry.primary = { link: l, centerRule };
                entry.centerRule = centerRule;
            }
            byOther.set(otherId, entry);
        });
        return [...byOther.values()];
    }, [centerSopId]);

    const positions = satellitePositions(adjacencies.length);

    if (!center) return null;
    const centerDms = DMS[center.dms];

    return (
        <div
            className="flex-1 relative overflow-hidden bg-white"
            data-testid="spotlight-canvas"
        >
            <svg
                viewBox={`0 0 ${SPOTLIGHT_W} ${SPOTLIGHT_H}`}
                className="absolute inset-0 w-full h-full"
                preserveAspectRatio="xMidYMid meet"
            >
                {/* faint dotted backdrop */}
                <defs>
                    <pattern
                        id="dotbg"
                        width="22"
                        height="22"
                        patternUnits="userSpaceOnUse"
                    >
                        <circle cx="1" cy="1" r="1" fill="#E4E4E7" />
                    </pattern>
                </defs>
                <rect
                    width={SPOTLIGHT_W}
                    height={SPOTLIGHT_H}
                    fill="url(#dotbg)"
                    opacity="0.35"
                />

                {/* Edges */}
                {adjacencies.map(({ primary: { link, centerRule } }, i) => {
                    const sp = positions[i];
                    const r = RISK[link.risk];
                    const selected = link.id === selectedLinkId;
                    // Start: satellite card right edge, anchor vertically mid
                    const start = {
                        x:
                            sp.x < CENTER.x
                                ? sp.x + SATELLITE_CARD.w / 2
                                : sp.x - SATELLITE_CARD.w / 2,
                        y: sp.y,
                    };
                    // End: land on the correct rule row inside the center card
                    // rule row y = top of card + 70 (header) + 38 * rule_index
                    const ruleIndex = center.rules.findIndex(
                        (rr) => rr.id === centerRule,
                    );
                    const endY =
                        CENTER.y -
                        CENTER_CARD.h / 2 +
                        70 +
                        (ruleIndex >= 0 ? ruleIndex : center.rules.length / 2) *
                            38 +
                        19;
                    const end = {
                        x:
                            sp.x < CENTER.x
                                ? CENTER.x - CENTER_CARD.w / 2
                                : CENTER.x + CENTER_CARD.w / 2,
                        y: endY,
                    };
                    const midX = (start.x + end.x) / 2;
                    const path = `M ${start.x} ${start.y} C ${midX} ${start.y}, ${midX} ${end.y}, ${end.x} ${end.y}`;
                    return (
                        <g key={link.id}>
                            <path
                                d={path}
                                fill="none"
                                stroke={r.color}
                                strokeWidth={selected ? 3 : 2}
                                strokeDasharray={
                                    r.lineStyle === "dashed"
                                        ? "8 6"
                                        : r.lineStyle === "dotted"
                                          ? "2 5"
                                          : "0"
                                }
                                opacity={
                                    selectedLinkId && !selected ? 0.25 : 0.9
                                }
                                style={{
                                    cursor: "pointer",
                                    transition: "opacity 0.2s",
                                }}
                                onClick={() => onSelectLink(link.id)}
                            />
                            {/* risk label chip floating mid-edge */}
                            <foreignObject
                                x={midX - 60}
                                y={(start.y + end.y) / 2 - 12}
                                width={120}
                                height={24}
                                style={{ pointerEvents: "none" }}
                            >
                                <div
                                    className="flex justify-center"
                                    style={{
                                        pointerEvents: "auto",
                                    }}
                                >
                                    <span
                                        onClick={() => onSelectLink(link.id)}
                                        className="mono text-[9px] uppercase tracking-wider px-1.5 py-[1px] border rounded-sm cursor-pointer"
                                        style={{
                                            color: r.color,
                                            borderColor: r.color + "66",
                                            background: "#FFFFFF",
                                        }}
                                    >
                                        {r.label}
                                    </span>
                                </div>
                            </foreignObject>
                        </g>
                    );
                })}
            </svg>

            {/* Center SOP card */}
            <div
                className="absolute bg-white border border-hair shadow-none"
                style={{
                    left: `${((CENTER.x - CENTER_CARD.w / 2) / SPOTLIGHT_W) * 100}%`,
                    top: `${((CENTER.y - CENTER_CARD.h / 2) / SPOTLIGHT_H) * 100}%`,
                    width: `${(CENTER_CARD.w / SPOTLIGHT_W) * 100}%`,
                    height: `${(CENTER_CARD.h / SPOTLIGHT_H) * 100}%`,
                    borderColor: centerDms.accent,
                    borderWidth: 2,
                }}
                data-testid="spotlight-center"
            >
                <div
                    className="px-4 py-3 border-b flex items-center justify-between gap-2"
                    style={{ borderColor: centerDms.accentBorder, background: centerDms.accentBg }}
                >
                    <div className="min-w-0">
                        <div className="overline text-[9px] mb-1">
                            {center.id} · rev {center.revision}
                        </div>
                        <div className="heading text-[15px] font-medium tracking-tight truncate">
                            {center.title}
                        </div>
                    </div>
                    <DmsBadge dmsId={center.dms} size="lg" />
                </div>
                <div>
                    {center.rules.map((r) => {
                        // highlight rule when a selected link hits it
                        const selLink = LINKS.find(
                            (l) => l.id === selectedLinkId,
                        );
                        const ruleHit =
                            selLink &&
                            ((selLink.from === center.id &&
                                selLink.from_rule === r.id) ||
                                (selLink.to === center.id &&
                                    selLink.to_rule === r.id));
                        return (
                            <RuleRow
                                key={r.id}
                                rule={r}
                                highlighted={ruleHit}
                            />
                        );
                    })}
                </div>
            </div>

            {/* Satellite SOP cards */}
            {adjacencies.map(({ other, primary: { link }, links }, i) => {
                const sp = positions[i];
                const d = DMS[other.dms];
                const extra = links.length - 1;
                return (
                    <button
                        key={other.id}
                        type="button"
                        onClick={() => {
                            onFocusSop(other.id);
                            onSelectLink(null);
                        }}
                        data-testid={`satellite-${other.id}`}
                        className="absolute text-left bg-white border border-hair hover:border-neutral-400 transition-colors"
                        style={{
                            left: `${((sp.x - SATELLITE_CARD.w / 2) / SPOTLIGHT_W) * 100}%`,
                            top: `${((sp.y - SATELLITE_CARD.h / 2) / SPOTLIGHT_H) * 100}%`,
                            width: `${(SATELLITE_CARD.w / SPOTLIGHT_W) * 100}%`,
                            height: `${(SATELLITE_CARD.h / SPOTLIGHT_H) * 100}%`,
                            borderLeftColor: d.accent,
                            borderLeftWidth: 3,
                        }}
                        title={`Make ${other.id} the spotlight`}
                    >
                        <div className="px-3 py-2 flex items-center justify-between gap-2">
                            <span className="mono text-[10px] text-[#C2410C]">
                                {other.id}
                            </span>
                            <DmsBadge dmsId={other.dms} />
                        </div>
                        <div className="px-3 text-[12px] heading tracking-tight font-medium line-clamp-2">
                            {other.title}
                        </div>
                        <div className="px-3 mt-auto pb-2 pt-2 flex items-center justify-between">
                            <span className="mono text-[10px] text-neutral-500">
                                rev {other.revision}
                            </span>
                            <span className="text-[10px] text-neutral-400 inline-flex items-center gap-1">
                                <RiskPill riskId={link.risk} />
                                {extra > 0 && (
                                    <span className="mono text-[9px] text-neutral-500">
                                        +{extra}
                                    </span>
                                )}
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

// ---------- DMS strip (bottom chrome) ----------

const DmsStrip = () => (
    <div className="border-t border-hair bg-white px-6 py-3 flex items-center gap-6 overflow-x-auto">
        <div className="overline flex items-center gap-2 shrink-0">
            <Database size={11} /> Source silos
        </div>
        {Object.values(DMS).map((d) => {
            const count = SOPS.filter((s) => s.dms === d.id).length;
            return (
                <div
                    key={d.id}
                    className="flex items-center gap-2 shrink-0"
                    data-testid={`dms-strip-${d.id}`}
                >
                    <span
                        className="w-[8px] h-[8px] rounded-sm"
                        style={{ background: d.accent }}
                    />
                    <span className="text-[12px] heading tracking-tight font-medium">
                        {d.name}
                    </span>
                    <span className="mono text-[10px] text-neutral-500">
                        {count} SOP{count === 1 ? "" : "s"}
                    </span>
                </div>
            );
        })}
        <div className="ml-auto text-[11px] text-neutral-500 italic hidden md:block">
            Even Veeva modules don't always talk to each other.
        </div>
    </div>
);

// ---------- drill panel (when a link is selected) ----------

const DrillPanel = ({ linkId, onClose }) => {
    const link = LINKS.find((l) => l.id === linkId);
    if (!link) return null;
    const risk = RISK[link.risk];
    const from = SOPS.find((s) => s.id === link.from);
    const to = SOPS.find((s) => s.id === link.to);
    const fromRule = from?.rules.find((r) => r.id === link.from_rule);
    const toRule = to?.rules.find((r) => r.id === link.to_rule);
    return (
        <div
            className="border-t border-hair bg-[#fafafa] px-6 py-5"
            data-testid="drill-panel"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="overline flex items-center gap-2 mb-2">
                        <Zap size={11} /> Why this matters
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <RiskPill riskId={link.risk} />
                        <span className="text-[13px] heading tracking-tight font-medium">
                            {from?.id} → {to?.id}
                        </span>
                        <DmsBadge dmsId={from?.dms} />
                        <span className="text-neutral-300 text-[10px]">↔</span>
                        <DmsBadge dmsId={to?.dms} />
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-[11px] text-neutral-500 hover:text-[#0A0A0A] mono"
                >
                    close ×
                </button>
            </div>
            <p className="text-[13px] text-neutral-700 mt-3 leading-relaxed max-w-3xl">
                {link.note}
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div className="border border-hair bg-white p-4">
                    <div className="overline mb-2">
                        {from?.id} · rev {from?.revision}
                    </div>
                    <div className="heading text-[13px] font-medium tracking-tight mb-3">
                        {from?.title}
                    </div>
                    {fromRule && (
                        <div className="text-[12px] border-l-2 border-[#C2410C] pl-3 text-neutral-700 leading-relaxed">
                            <span className="mono text-[10px] text-[#C2410C] mr-2">
                                {fromRule.id}
                            </span>
                            {fromRule.text}
                        </div>
                    )}
                </div>
                <div className="border border-hair bg-white p-4">
                    <div className="overline mb-2">
                        {to?.id} · rev {to?.revision}
                    </div>
                    <div className="heading text-[13px] font-medium tracking-tight mb-3">
                        {to?.title}
                    </div>
                    {toRule ? (
                        <div className="text-[12px] border-l-2 border-[#C2410C] pl-3 text-neutral-700 leading-relaxed">
                            <span className="mono text-[10px] text-[#C2410C] mr-2">
                                {toRule.id}
                            </span>
                            {toRule.text}
                        </div>
                    ) : (
                        <div className="text-[12px] text-neutral-500 italic border-l-2 border-[#B91C1C] pl-3">
                            Referenced rule no longer resolves (cited SOP was
                            deprecated).
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-xs px-3 py-2 rounded-sm hover:bg-[#9A3412]"
                >
                    Open remediation task
                    <ChevronRight size={12} />
                </button>
                <button
                    type="button"
                    className="inline-flex items-center gap-2 border border-hair text-xs px-3 py-2 rounded-sm hover:bg-white"
                >
                    Assign owner
                </button>
                <span className="ml-auto text-[11px] text-neutral-500 mono">
                    Severity · {risk.severity}
                </span>
            </div>
        </div>
    );
};

// ---------- page ----------

export default function LinkGraph() {
    const [centerSopId, setCenterSopId] = useState("SOP-DI-004");
    const [selectedLinkId, setSelectedLinkId] = useState("L2");

    return (
        <AppLayout>
            <PageHeader
                eyebrow="Workspace · Concept"
                title="Link Graph"
                sub="Your SOP corpus lives across Veeva, SharePoint, Documentum and TrackWise. Here's where the silos disagree — ranked by audit exposure, before an inspector finds them."
                right={
                    <div className="flex items-center gap-2 text-[11px] mono uppercase tracking-wider text-neutral-500">
                        <Info size={11} />
                        Concept mock · seeded data
                    </div>
                }
            />

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 border-l border-hair">
                <Stat
                    label="Pre-audit score"
                    value={`${READINESS.score}%`}
                    sub="Corpus readiness"
                    accent
                    testid="lg-score"
                />
                <Stat
                    label="Broken links"
                    value={READINESS.broken_links}
                    sub={`${READINESS.critical_breaks} critical`}
                    testid="lg-broken"
                />
                <Stat
                    label="Stale refs"
                    value={READINESS.stale_refs}
                    sub="Older revision cited"
                    testid="lg-stale"
                />
                <Stat
                    label="DMS silos"
                    value={READINESS.systems_affected}
                    sub="Sources of record touched"
                    testid="lg-silos"
                />
                <Stat
                    label="SOPs at risk"
                    value={READINESS.sops_at_risk}
                    sub={`of ${SOPS.length} indexed`}
                    testid="lg-sops-at-risk"
                />
            </div>

            {/* Triage + Spotlight */}
            <div
                className="grid grid-cols-1 md:grid-cols-[340px_1fr] border-b border-hair"
                style={{ minHeight: 600 }}
            >
                <TriageRail
                    links={LINKS}
                    selectedLinkId={selectedLinkId}
                    onSelectLink={setSelectedLinkId}
                    onFocusSop={setCenterSopId}
                />
                <Spotlight
                    centerSopId={centerSopId}
                    onFocusSop={setCenterSopId}
                    selectedLinkId={selectedLinkId}
                    onSelectLink={setSelectedLinkId}
                />
            </div>

            {selectedLinkId && (
                <DrillPanel
                    linkId={selectedLinkId}
                    onClose={() => setSelectedLinkId(null)}
                />
            )}

            <DmsStrip />
        </AppLayout>
    );
}
